import { getClientEnv } from "@shared/env";
import type { RefreshResponse } from "@shared/types";
import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "@/modules/auth/store/auth.store";
import { getStoredAccessToken, getStoredRefreshToken } from "@/shared/utils/token";
import { shouldLetBrowserSetMultipartBoundary, stripContentTypeHeader } from "./form-data";

const env = getClientEnv();

export const apiClient = axios.create({
	baseURL: `${env.VITE_API_URL}/api`,
	headers: {
		"Content-Type": "application/json",
	},
});

// Request interceptor - attach access token
apiClient.interceptors.request.use(
	(config: InternalAxiosRequestConfig) => {
		if (shouldLetBrowserSetMultipartBoundary(config.data)) {
			stripContentTypeHeader(config.headers);
		}

		const accessToken = useAuthStore.getState().accessToken;
		if (accessToken) {
			config.headers.Authorization = `Bearer ${accessToken}`;
		}
		return config;
	},
	(error) => Promise.reject(error)
);

// Refresh token mutex — ensures only one refresh call runs at a time.
// All parallel 401 responses await the same promise instead of each
// triggering their own refresh (which would invalidate the previous one).
let refreshPromise: Promise<string> | null = null;

function doRefresh(): Promise<string> {
	const refreshToken = useAuthStore.getState().refreshToken;
	if (!refreshToken) {
		return Promise.reject(new Error("No refresh token"));
	}

	return axios
		.post<RefreshResponse>(`${env.VITE_API_URL}/api/auth/refresh`, { refreshToken })
		.then((response) => {
			const { accessToken, refreshToken: newRefreshToken } = response.data.data;
			useAuthStore.getState().setTokens(accessToken, newRefreshToken);
			return accessToken;
		});
}

// Response interceptor - handle 401 and auto-refresh
apiClient.interceptors.response.use(
	(response) => response,
	async (error: AxiosError) => {
		const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

		// If 401 and not a retry, try to refresh token
		if (error.response?.status === 401 && !originalRequest._retry) {
			originalRequest._retry = true;

			const currentRefreshToken = useAuthStore.getState().refreshToken;
			if (!currentRefreshToken) {
				useAuthStore.getState().logout();
				window.location.href = "/login";
				return Promise.reject(error);
			}

			try {
				// If a refresh is already in progress, wait for it.
				// Otherwise, start one and store the promise so others can join.
				if (!refreshPromise) {
					refreshPromise = doRefresh().finally(() => {
						refreshPromise = null;
					});
				}

				const newAccessToken = await refreshPromise;

				// Retry original request with new token
				originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
				return apiClient(originalRequest);
			} catch {
				// Refresh failed — check if another tab already refreshed
				const latestAccessToken = getStoredAccessToken();
				const latestRefreshToken = getStoredRefreshToken();
				if (latestAccessToken && latestRefreshToken && latestRefreshToken !== currentRefreshToken) {
					// Another tab refreshed — sync and retry the original request
					useAuthStore.getState().setTokens(latestAccessToken, latestRefreshToken);
					originalRequest.headers.Authorization = `Bearer ${latestAccessToken}`;
					return apiClient(originalRequest);
				}
				// Truly failed — logout user
				useAuthStore.getState().logout();
				window.location.href = "/login";
				return Promise.reject(error);
			}
		}

		return Promise.reject(error);
	}
);

export default apiClient;
